import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../../../../../components/ui/dialog';
import { Sparkles, Loader2 } from 'lucide-react';
import { getApiBaseUrl } from '../../../../../../config/apiConfig';

export default function PromptGeneratorModal({ isOpen, onClose, onGenerate }) {
    const [formData, setFormData] = useState({
        url: '',
        businessName: '',
        industry: '',
        targetAudience: '',
        goal: '',
        tone: 'Professional',
        agentName: '',
        language: 'English',
        email: ''
    });
    const [constraintsInput, setConstraintsInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleGenerate = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const payload = {
                ...formData,
                userConstraints: constraintsInput.split('\n').filter(Boolean)
            };

            const response = await fetch(`${getApiBaseUrl()}/api/chatbot/generate-system-prompt`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to generate prompt');
            if (data.prompt) {
                onGenerate(data.prompt);
                onClose(); // Auto close on success
            } else {
                throw new Error('No prompt returned from server');
            }
        } catch (err) {
            setError(err.message || "Something went wrong");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(val) => !val && onClose()}>
            <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-indigo-500" />
                        AI Prompt Builder
                    </DialogTitle>
                    <DialogDescription>
                        Enter your business details to auto-generate a comprehensive system prompt.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-700">Website URL</label>
                        <input
                            name="url"
                            placeholder="https://example.com"
                            value={formData.url}
                            onChange={handleChange}
                            className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <p className="text-[10px] text-slate-500">We will analyze your website to structure the knowledge base navigation.</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-slate-700">Company Name</label>
                            <input name="businessName" placeholder="My Company" value={formData.businessName} onChange={handleChange} className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-slate-700">Support Email</label>
                            <input name="email" placeholder="support@example.com" value={formData.email} onChange={handleChange} className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-slate-700">Agent Name</label>
                            <input name="agentName" placeholder="Bella" value={formData.agentName} onChange={handleChange} className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-slate-700">Tone</label>
                            <select name="tone" value={formData.tone} onChange={handleChange} className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                <option>Professional</option>
                                <option>Friendly</option>
                                <option>Enthusiastic</option>
                                <option>Empathetic</option>
                                <option>Strict</option>
                            </select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-700">Max Response Length (Words)</label>
                        <input type="number" name="responseLength" placeholder="100" value={formData.responseLength} onChange={handleChange} className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>

                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-700">Do's & Don'ts (One per line)</label>
                        <textarea
                            value={constraintsInput}
                            onChange={(e) => setConstraintsInput(e.target.value)}
                            placeholder="e.g. Do not mention pricing&#10;Always be polite"
                            className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[60px]"
                        />
                    </div>

                    {error && <p className="text-red-500 text-xs bg-red-50 p-2 rounded">{error}</p>}
                </div>

                <DialogFooter>
                    <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors">Cancel</button>
                    <button
                        onClick={handleGenerate}
                        disabled={isLoading}
                        className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-md flex items-center gap-2 disabled:opacity-50 transition-colors shadow-sm"
                    >
                        {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                        {isLoading ? 'Generating...' : 'Generate Prompt'}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
